import { showErrorToast, showSuccessToast } from "../theme/create_toast/GenericToaster";

export function parseOrderTimestamp(timestamp) {
  if (!timestamp) return new Date();

  if (typeof timestamp === "string" && timestamp.includes(".")) {
    const parts = timestamp.split(".");
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    const hour = parseInt(parts[3], 10) || 0;
    const minute = parseInt(parts[4], 10) || 0;
    const second = parseInt(parts[5], 10) || 0;
    const ms = parseInt(parts[6], 10) || 0;

    if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      // PoMS SimpleDateFormat is JVM local wall clock, not UTC.
      const date = new Date(year, month - 1, day, hour, minute, second, ms);
      if (!Number.isNaN(date.getTime())) return date;
    }
  }

  const parsed = new Date(timestamp);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  return new Date();
}

export function formatReceiptDateKey(timestamp) {
  const date = parseOrderTimestamp(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatReceiptDateTime(timestamp) {
  const date = timestamp ? parseOrderTimestamp(timestamp) : new Date();
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hours = date.getHours();
  const mins = String(date.getMinutes()).padStart(2, "0");
  const secs = String(date.getSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const h12 = String(hours % 12 || 12).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${h12}:${mins}:${secs} ${ampm}`;
}

export function formatOrderDisplayDateTime(timestamp) {
  if (!timestamp) return "";
  const date = parseOrderTimestamp(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = String(hours % 12 || 12).padStart(2, "0");
  return `${year}.${month}.${day} at ${hour12}:${minutes} ${ampm}`;
}

function findProductForLineItem(products, item) {
  const productId = item?.productId ?? item?.id;
  if (!productId || !Array.isArray(products)) return null;
  return products.find(
    (p) =>
      String(p.id) === String(productId) ||
      String(p.POID) === String(productId)
  );
}

/** Active sold lines only — excludes returned rows duplicated in lineItems. */
export function getOrderSaleLineItems(order) {
  if (!order) return [];
  if (Array.isArray(order.soldLineItems) && order.soldLineItems.length > 0) {
    return order.soldLineItems;
  }
  return (order.lineItems || []).filter((item) => !item?.returned);
}

export function getOrderReturnedLineItems(order) {
  if (!order) return [];
  if (Array.isArray(order.returnedLineItems) && order.returnedLineItems.length > 0) {
    return order.returnedLineItems;
  }
  return (order.lineItems || []).filter((item) => item?.returned);
}

export function getOrderSoldItemCount(order) {
  return getOrderSaleLineItems(order).length;
}

export function getOrderSoldQuantityTotal(order) {
  return getOrderSaleLineItems(order).reduce(
    (sum, item) => sum + Number(item.quantity ?? item.units ?? item.qty ?? 0),
    0
  );
}

export function resolveLineItemDiscountRate(item, products = []) {
  const raw = item?.discountRate ?? item?.discount;
  if (raw != null && raw !== "" && !Number.isNaN(Number(raw)) && Number(raw) > 0) {
    return Number(raw);
  }
  const product = findProductForLineItem(products, item);
  return Number(product?.discountRate || 0);
}

/** Reconcile API order totals with product-level discounts and order-level discount. */
export function enrichOrderFinancials(order, products = []) {
  if (!order) return order;

  const saleLineItems = getOrderSaleLineItems(order);
  const itemsForCalc = saleLineItems.length ? saleLineItems : order.lineItems || [];
  if (!itemsForCalc.length) {
    const apiTotal = Number(order.total ?? 0);
    const apiPayable = Number(order.payableAmount ?? 0);
    if (apiTotal > 0 || apiPayable > 0) {
      const payable = apiPayable > 0 ? apiPayable : apiTotal;
      const total = apiTotal > 0 ? apiTotal : payable;
      return {
        ...order,
        total,
        payableAmount: payable,
        netAmountAfterReturn: payable,
      };
    }
    return order;
  }

  const enrichedSaleItems = itemsForCalc.map((item) => ({
    ...item,
    discountRate: resolveLineItemDiscountRate(item, products),
  }));

  const mapped = mapOrderLineItems(enrichedSaleItems);
  const storedOrderDiscount = Number(order.discountAmount || 0);
  const calcBase = calculateReceiptTotals(mapped, 0);
  const apiPayable = Number(order.payableAmount ?? 0);

  let orderDiscountAmount = storedOrderDiscount;
  if (orderDiscountAmount <= 0 && apiPayable > 0) {
    const gap = calcBase.payableAmount - apiPayable;
    if (gap > 0.005) {
      orderDiscountAmount = Number(gap.toFixed(2));
    }
  }

  const totals = calculateReceiptTotals(mapped, orderDiscountAmount);
  const returnFinancials = calculateOrderReturnedFinancials(order, products, {
    totalAmount: totals.totalAmount,
    payableAmount: totals.payableAmount,
    orderDiscountAmount,
  });
  const returnedAmount = returnFinancials.returnedAmount;
  const netAmountAfterReturn =
    returnFinancials.netAmountAfterReturn ??
    (order.returned && order.netAmountAfterReturn != null
      ? Number(order.netAmountAfterReturn)
      : totals.payableAmount - returnedAmount);

  return {
    ...order,
    soldLineItems: enrichedSaleItems,
    lineItems: enrichedSaleItems,
    subTotal: totals.subtotal,
    taxAmount: totals.taxAmount,
    total: totals.totalAmount,
    discountAmount: orderDiscountAmount,
    lineDiscountTotal: totals.lineDiscountTotal,
    totalDiscountAmount: totals.lineDiscountTotal + orderDiscountAmount,
    payableAmount: totals.payableAmount,
    returnedAmount,
    netAmountAfterReturn,
    amountReceived: order.paid
      ? Number(order.amountReceived ?? totals.payableAmount)
      : order.amountReceived,
  };
}

export function calculateReturnRefundTotals(returnItems = [], products = []) {
  const enriched = (returnItems || []).map((item) => ({
    ...item,
    discountRate: resolveLineItemDiscountRate(item, products),
  }));
  return calculateReceiptTotals(mapOrderLineItems(enriched), 0);
}

/** Refund total for returned items, including proportional order-level discount. */
export function calculateOrderReturnedFinancials(order, products = [], saleTotals = null) {
  const returnedItems = getOrderReturnedLineItems(order);
  if (!returnedItems.length) {
    return {
      returnedAmount: Number(order?.returnedAmount || 0),
      netAmountAfterReturn:
        order?.netAmountAfterReturn != null
          ? Number(order.netAmountAfterReturn)
          : null,
      returnTotals: null,
      orderDiscountAdjustment: 0,
    };
  }

  const returnTotals = calculateReturnRefundTotals(returnedItems, products);
  const orderTotal = saleTotals?.totalAmount ?? Number(order?.total || 0);
  const orderDiscountAmount = saleTotals?.orderDiscountAmount ?? Number(order?.discountAmount || 0);
  const orderPayable = saleTotals?.payableAmount ?? Number(order?.payableAmount || 0);

  let orderDiscountAdjustment = 0;
  if (orderDiscountAmount > 0 && orderTotal > 0) {
    orderDiscountAdjustment = (returnTotals.totalAmount / orderTotal) * orderDiscountAmount;
  }

  const returnedAmount = Math.max(0, returnTotals.payableAmount - orderDiscountAdjustment);
  const netAmountAfterReturn = Math.max(0, orderPayable - returnedAmount);

  return {
    returnedAmount: Number(returnedAmount.toFixed(2)),
    netAmountAfterReturn: Number(netAmountAfterReturn.toFixed(2)),
    returnTotals,
    orderDiscountAdjustment: Number(orderDiscountAdjustment.toFixed(2)),
  };
}

export function formatReceiptDotTimestamp(date = new Date()) {
  return [
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  ].join(".");
}

export function formatReceiptMoney(value) {
  const num = Number(value || 0);
  return Math.round(num).toLocaleString("en-PK");
}

async function fetchLogoAsDataUrl(url) {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

async function fetchLogoViaProxy(url, configuredPrinterUrl = null) {
  const proxyUrls = [];

  if (typeof window !== "undefined") {
    // HOMS dev server — fetches images in Node, no PrinterDesktop required
    proxyUrls.push(`/receipt-logo-proxy?url=${encodeURIComponent(url)}`);
    proxyUrls.push(`/proxy-image?url=${encodeURIComponent(url)}`);
  }

  const { healthUrl } = resolvePrinterServiceUrls(configuredPrinterUrl);
  const origin = healthUrl.replace(/\/health$/, "");
  const printerProxy = `${origin}/proxy-image?url=${encodeURIComponent(url)}`;
  if (!proxyUrls.includes(printerProxy)) {
    proxyUrls.push(printerProxy);
  }

  for (const proxyUrl of proxyUrls) {
    try {
      const proxyResponse = await fetch(proxyUrl, { method: "GET" });
      if (!proxyResponse.ok) continue;
      const data = await proxyResponse.json().catch(() => ({}));
      if (data.dataUrl) return data.dataUrl;
    } catch {
      // try next proxy endpoint
    }
  }

  return null;
}

function isCrossOriginStorageUrl(url) {
  try {
    const parsed = new URL(url);
    if (typeof window !== "undefined" && parsed.origin === window.location.origin) {
      return false;
    }
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export async function getLogoBase64(url, configuredPrinterUrl = null) {
  if (!url) return null;
  if (url.startsWith("data:image")) return url;

  if (isCrossOriginStorageUrl(url)) {
    return fetchLogoViaProxy(url, configuredPrinterUrl);
  }

  try {
    return await fetchLogoAsDataUrl(url);
  } catch {
    return fetchLogoViaProxy(url, configuredPrinterUrl);
  }
}

export async function prepareReceiptMetadataWithLogo(receiptMetadata, configuredPrinterUrl = null) {
  if (!receiptMetadata?.logoUrl) return receiptMetadata || {};
  const copy = { ...receiptMetadata };
  const logoBase64 = await getLogoBase64(copy.logoUrl, configuredPrinterUrl);
  if (logoBase64) copy.logoUrl = logoBase64;
  return copy;
}

export function mapCartItemsToCalcLineItems(cartItems = []) {
  return (cartItems || []).map((item) => ({
    name: item.name,
    price: Number(item.price || 0),
    quantity: Number(item.units || 0),
    units: Number(item.units || 0),
    taxRate: Number(item.taxRate || 0),
    discountRate: Number(item.discountRate || 0),
  }));
}

export function mapOrderLineItems(lineItems = []) {
  return lineItems.map((item) => ({
    name: item.productName || item.name || "Product",
    price: Number(item.price || 0),
    units: Number(item.quantity ?? item.units ?? item.qty ?? 0),
    quantity: Number(item.quantity ?? item.units ?? item.qty ?? 0),
    taxRate: Number(item.tax || item.taxRate || 0),
    discountRate: Number(item.discountRate || item.discount || 0),
    detail: item.detail || item.sku || "",
  }));
}

/** Matches useOrderCalculations — line discounts before tax, then order-level discount. */
export function calculateReceiptTotals(lineItems = [], orderDiscountAmount = 0) {
  const subtotal = lineItems.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || item.units || 0),
    0
  );

  const lineDiscountTotal = lineItems.reduce((sum, item) => {
    const lineSubtotal = Number(item.price || 0) * Number(item.quantity || item.units || 0);
    const rate = Number(item.discountRate || 0);
    return sum + (lineSubtotal * rate) / 100;
  }, 0);

  const taxAmount = lineItems.reduce((sum, item) => {
    const lineSubtotal = Number(item.price || 0) * Number(item.quantity || item.units || 0);
    const rate = Number(item.discountRate || 0);
    const discountedSubtotal = lineSubtotal - (lineSubtotal * rate) / 100;
    const taxRate = Number(item.tax || item.taxRate || 0);
    return sum + (discountedSubtotal * taxRate) / 100;
  }, 0);

  const totalAmount = subtotal - lineDiscountTotal + taxAmount;
  const payableAmount = Math.max(0, totalAmount - Number(orderDiscountAmount || 0));

  return {
    subtotal: Number(subtotal.toFixed(2)),
    lineDiscountTotal: Number(lineDiscountTotal.toFixed(2)),
    taxAmount: Number(taxAmount.toFixed(2)),
    totalAmount: Number(totalAmount.toFixed(2)),
    payableAmount: Number(payableAmount.toFixed(2)),
  };
}

export async function buildSalesReceiptPrintPayload({
  order,
  lineItems,
  customer,
  receiptMeta,
  printerSettings,
  paymentType = "cash",
  amountReceived = 0,
  discountAmount = 0,
  embedLogo = false,
}) {
  const mappedItems = mapOrderLineItems(lineItems);
  const orderDiscount = Number(discountAmount || order?.discountAmount || 0);
  const totals = calculateReceiptTotals(mappedItems, orderDiscount);
  const payableAmount = Number(order?.payableAmount ?? totals.payableAmount);
  const receiptMetaCopy = { ...(receiptMeta || {}) };
  const printerUrl = printerSettings?.printerUrlAddress;

  if (embedLogo && receiptMetaCopy.logoUrl) {
    const logoBase64 = await getLogoBase64(receiptMetaCopy.logoUrl, printerUrl);
    if (logoBase64) receiptMetaCopy.logoUrl = logoBase64;
  }

  const paid = !!order?.paid;
  const received = paid
    ? Number(amountReceived || order?.amountReceived || payableAmount)
    : 0;

  return {
    pageSize: printerSettings?.salesprinterPaperWidth || "80mm",
    createdAt: formatReceiptDateTime(order?.createdAt),
    pItems: mappedItems,
    barCode: order?.orderId,
    companyInfo: receiptMetaCopy,
    type: "SALES",
    token: printerSettings?.printerreciepttoken || false,
    paid,
    fulfilled: !!order?.fulfilled,
    paymentType: (paymentType || "cash").toUpperCase(),
    amountReceived: received,
    discountAmount: orderDiscount,
    lineDiscountTotal: totals.lineDiscountTotal,
    subtotal: totals.subtotal,
    taxAmount: totals.taxAmount,
    totalAmount: totals.totalAmount,
    payableAmount,
    customerName: customer?.name || customer?.displayName || "Walk-in Customer",
    customerPhone: customer?.phoneNumber || customer?.mobile || "",
    customerEmail: customer?.emailAddress || customer?.email || "",
  };
}

export async function buildRefundReceiptPrintPayload({
  order,
  refundLineItems,
  refundSummary,
  customer,
  receiptMeta,
  printerSettings,
  embedLogo = false,
  pageSize: pageSizeOverride,
}) {
  const mappedItems = mapOrderLineItems(refundLineItems);
  const receiptMetaCopy = { ...(receiptMeta || {}) };
  const printerUrl = printerSettings?.printerUrlAddress;
  const pageSize =
    pageSizeOverride ||
    printerSettings?.salesprinterPaperWidth ||
    receiptMetaCopy?.printerPaperWidth ||
    "80mm";

  if (embedLogo && receiptMetaCopy.logoUrl) {
    const logoBase64 = await getLogoBase64(receiptMetaCopy.logoUrl, printerUrl);
    if (logoBase64) receiptMetaCopy.logoUrl = logoBase64;
  }

  return {
    pageSize,
    createdAt: formatReceiptDateTime(order?.createdAt),
    pItems: mappedItems,
    barCode: order?.orderId,
    companyInfo: receiptMetaCopy,
    type: "REFUND",
    token: false,
    orderSubTotal: Number(order?.subTotal || 0),
    orderLineDiscountTotal: Number(order?.lineDiscountTotal || 0),
    orderTaxAmount: Number(order?.taxAmount || 0),
    orderTotal: Number(order?.total || 0),
    orderDiscountAmount: Number(order?.discountAmount || 0),
    orderPayableAmount: Number(order?.payableAmount || 0),
    refundSubTotal: Number(refundSummary?.subTotalAmount || 0),
    refundLineDiscountTotal: Number(refundSummary?.lineDiscountTotal || 0),
    refundTaxAmount: Number(refundSummary?.taxAmount || 0),
    refundTotal: Number(refundSummary?.totalAmount || 0),
    discountAdjustment: Number(refundSummary?.discountAdjustment || 0),
    refundableAmount: Number(refundSummary?.refundableAmount || 0),
    customerName: customer?.name || customer?.displayName || "",
    customerPhone: customer?.phoneNumber || customer?.mobile || "",
    customerEmail: customer?.emailAddress || customer?.email || "",
  };
}

export const DEFAULT_PRINTER_SERVICE_URL = "http://127.0.0.1:3002/printserver";

/** Fix common printer URL typos (e.g. localhost;3002) and map API field names. */
export function normalizePrinterServiceUrl(raw) {
  if (!raw) return "";

  let value = String(raw).trim();
  if (!value) return "";

  value = value.replace(/;(3\d{3})(?=\/|$)/, ":$1");
  value = value.replace(/localhost;(\d+)/gi, "localhost:$1");
  value = value.replace(/127\.0\.0\.1;(\d+)/g, "127.0.0.1:$1");

  if (!/^https?:\/\//i.test(value)) {
    value = `http://${value}`;
  }

  try {
    new URL(value);
    return value;
  } catch {
    return "";
  }
}

export function normalizePrinterSettings(printerSettings) {
  const source = printerSettings && typeof printerSettings === "object" ? printerSettings : {};
  const rawUrl =
    source.printerUrlAddress ||
    source.printerUrl ||
    source.printerURL ||
    "";

  const printerUrlAddress = normalizePrinterServiceUrl(rawUrl);

  return {
    ...source,
    printerUrlAddress,
    posprinterPaperWidth:
      source.posprinterPaperWidth ||
      source.printerPaperWidth ||
      "80mm",
    salesprinterPaperWidth:
      source.salesprinterPaperWidth ||
      source.printerPaperSalesWidth ||
      source.printerPaperWidth ||
      "80mm",
  };
}

export function getEffectivePrinterSettings(printerSettings) {
  const normalized = normalizePrinterSettings(printerSettings);
  if (normalized.printerUrlAddress) {
    return normalized;
  }
  return {
    ...normalized,
    printerUrlAddress: DEFAULT_PRINTER_SERVICE_URL,
  };
}

/** Resolve PrinterDesktop URLs — always hit port 3002 directly (CORS enabled on print service). */
export function resolvePrinterServiceUrls(configuredUrl) {
  const normalized = normalizePrinterServiceUrl(configuredUrl);
  const raw = normalized || DEFAULT_PRINTER_SERVICE_URL;

  try {
    const parsed = new URL(raw);
    const origin = parsed.origin;
    const printUrl = parsed.pathname.includes("printserver")
      ? raw
      : `${origin}/printserver`;

    return {
      printUrl,
      healthUrl: `${origin}/health`,
      printerStatusUrl: `${origin}/printer-status`,
    };
  } catch {
    return {
      printUrl: DEFAULT_PRINTER_SERVICE_URL,
      healthUrl: "http://127.0.0.1:3002/health",
      printerStatusUrl: "http://127.0.0.1:3002/printer-status",
    };
  }
}

export async function isPrinterServiceReachable(configuredUrl) {
  const { healthUrl } = resolvePrinterServiceUrls(configuredUrl);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const response = await fetch(healthUrl, { method: "GET", signal: controller.signal });
    clearTimeout(timeoutId);
    return response.ok;
  } catch {
    return false;
  }
}

/** True only when PrinterDesktop is running AND a real printer is configured and available. */
export async function isPrinterReadyForAutoPrint(configuredUrl, pageSize = "80mm") {
  const { printerStatusUrl } = resolvePrinterServiceUrls(configuredUrl);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    const url = `${printerStatusUrl}?pageSize=${encodeURIComponent(pageSize || "80mm")}`;
    const response = await fetch(url, { method: "GET", signal: controller.signal });
    clearTimeout(timeoutId);
    if (!response.ok) return false;

    const data = await response.json().catch(() => ({}));
    return data.ready === true;
  } catch {
    return false;
  }
}

export async function sendSalesReceiptToPrinter(payload, printerUrl, options = {}) {
  const { printUrl } = resolvePrinterServiceUrls(printerUrl);
  const { silentFailure = false, preview = false, silent = false } = options;

  try {
    const response = await fetch(printUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer my-token",
        "X-Print-Preview": preview ? "true" : "false",
        "X-Print-Silent": silent ? "true" : "false",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (response.ok && data.success !== false) {
      if (preview && !silent) {
        showSuccessToast("Receipt preview opened in PrinterDesktop");
      } else if (!silent) {
        showSuccessToast("Receipt sent to printer successfully");
      }
      return true;
    }

    if (!silentFailure) {
      showErrorToast(data.error || "Failed to send receipt to printer");
    }
    return false;
  } catch {
    if (!silentFailure) {
      showErrorToast("Cannot reach PrinterDesktop. Ensure the service is running on port 3002.");
    }
    return false;
  }
}

/**
 * Preload an image URL into browser memory/cache before triggering window.print().
 * Resolves immediately if no URL or data URI.
 * Resolves on image load, or on timeout/error fallback so print is never permanently blocked.
 */
export function preloadImage(url, timeoutMs = 3000) {
  if (!url) return Promise.resolve(null);
  if (typeof url === "string" && url.startsWith("data:image")) return Promise.resolve(url);
  return new Promise((resolve) => {
    const img = new Image();
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        resolve(url);
      }
    };
    const timer = setTimeout(finish, timeoutMs);
    img.onload = () => {
      clearTimeout(timer);
      finish();
    };
    img.onerror = () => {
      clearTimeout(timer);
      finish();
    };
    img.src = url;
    if (img.complete && img.naturalHeight > 0) {
      clearTimeout(timer);
      finish();
    }
  });
}
